import React, { useState } from "react";
import { Phone, ArrowRight, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, BUSINESS } from "../../lib/config";

export default function Hero() {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    service: "Backflow Testing",
    message: "",
  });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Name and phone are required.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", form);
      toast.success("Request received! We'll contact you shortly.");
      setForm({ name: "", phone: "", email: "", address: "", service: "Backflow Testing", message: "" });
    } catch (err) {
      toast.error("Could not submit request. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="top" className="relative hero-img" data-testid="hero-section">
      <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-start">
        {/* Left */}
        <div className="text-white anim-in">
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <span className="badge !bg-transparent !text-white !border-white/30">
              City of Laredo Certified
            </span>
            <span className="badge !bg-transparent !text-white !border-white/30">
              Licensed • Insured
            </span>
            <span className="badge !bg-transparent !text-white !border-white/30">
              Same-Day Service
            </span>
          </div>

          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
            Backflow Testing & <span className="text-orange-400">City Registration</span>,
            Done Right.
          </h1>

          <p className="mt-6 max-w-xl text-slate-200 text-base sm:text-lg leading-relaxed">
            Annual backflow testing, installation, repair, and city-of-Laredo paperwork — handled by certified
            techs. Fast scheduling. Reports emailed the same day.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a href={BUSINESS.phoneHref} className="btn-primary" data-testid="hero-call-btn">
              <Phone className="w-4 h-4" /> Call {BUSINESS.phone}
            </a>
            <a href="#booking" className="btn-ghost !text-white !border-white" data-testid="hero-book-btn">
              Book Appointment <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-0 border border-white/20 max-w-xl">
            {[
              { k: "8+", l: "Years in Laredo" },
              { k: "8,000+", l: "Tests & Repairs" },
              { k: "24h", l: "Avg Response" },
            ].map((s, i) => (
              <div
                key={i}
                className={`px-4 py-5 ${i < 2 ? "border-r border-white/20" : ""}`}
              >
                <div className="font-display text-3xl font-bold">{s.k}</div>
                <div className="text-[11px] tracking-[0.18em] uppercase text-slate-300 font-display mt-1">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Lead form card */}
        <form
          onSubmit={onSubmit}
          data-testid="hero-lead-form"
          className="bg-white border border-slate-200 p-6 lg:p-8 anim-in delay-2"
        >
          <div className="flex items-center gap-2 text-blue-600">
            <ShieldCheck className="w-4 h-4" />
            <span className="overline !text-blue-600">Free Quote · 2 Min Form</span>
          </div>
          <h3 className="font-display text-2xl font-bold mt-2 text-slate-900">
            Get Your Property Tested
          </h3>
          <p className="text-sm text-slate-600 mt-1">
            We'll text or call within the hour during business hours.
          </p>

          <div className="grid sm:grid-cols-2 gap-4 mt-5">
            <div>
              <label className="field-label">Full Name *</label>
              <input
                name="name"
                value={form.name}
                onChange={onChange}
                className="lb-input"
                required
                data-testid="lead-name-input"
              />
            </div>
            <div>
              <label className="field-label">Phone *</label>
              <input
                name="phone"
                type="tel"
                value={form.phone}
                onChange={onChange}
                className="lb-input"
                required
                data-testid="lead-phone-input"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="field-label">Email</label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                className="lb-input"
                data-testid="lead-email-input"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="field-label">Service Address</label>
              <input
                name="address"
                value={form.address}
                onChange={onChange}
                className="lb-input"
                placeholder="Street, City, TX"
                data-testid="lead-address-input"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="field-label">Service Needed</label>
              <select
                name="service"
                value={form.service}
                onChange={onChange}
                className="lb-select"
                data-testid="lead-service-select"
              >
                <option>Backflow Testing</option>
                <option>Backflow Installation</option>
                <option>Backflow Repair</option>
                <option>City of Laredo Registration</option>
                <option>Annual Re-certification</option>
                <option>Other</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="field-label">Notes</label>
              <textarea
                name="message"
                value={form.message}
                onChange={onChange}
                rows={3}
                className="lb-textarea"
                placeholder="Tell us about your device or city notice..."
                data-testid="lead-message-input"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full mt-5 justify-center disabled:opacity-60"
            data-testid="lead-submit-btn"
          >
            {loading ? "Sending..." : "Request Free Quote"} <ArrowRight className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-2 text-xs text-slate-500 mt-3">
            <CheckCircle2 className="w-3.5 h-3.5 text-blue-600" /> No spam. We only use your info to respond.
          </div>
        </form>
      </div>
    </section>
  );
}
